import type { LucideIcon } from 'lucide-react'
import {
  FALLBACK_SERVICE_ICON,
  SERVICE_CATEGORIES,
  type ServiceCategory,
} from '../data/service-taxonomy'

/**
 * Service routing and the homepage's featured shortlist.
 *
 * ── THIS FILE NO LONGER OWNS THE LIST ──────────────────────────────────────
 * It used to declare the eight categories inline. They now live in
 * `data/service-taxonomy`, which is the single source of service names,
 * slugs, copy, imagery and hierarchy — because the browse page, the listing
 * form, the search filters and this shortlist were all about to hold their
 * own copy, and four copies of a taxonomy drift within a release.
 *
 * What remains here is the routing, and the shortlist projected from the tree.
 * Every existing import keeps working unchanged.
 */

/**
 * A service category as the homepage presents it.
 *
 * Note the boundary this draws. These are FEATURED categories — an editorial
 * selection, not a popularity measurement and not the taxonomy. Today the
 * selection is "all eight", because there are only eight; when the real list
 * arrives, this stays a shortlist *of* it rather than becoming it.
 */
export interface FeaturedService {
  /** Display name, and the noun used in the card's action row. */
  name: string
  /** URL segment under /services. Must stay stable — it is a public URL. */
  slug: string
  /** One line. Deliberately short: these are signposts, not descriptions. */
  description: string
  icon: LucideIcon
}

export const FEATURED_SERVICES: readonly FeaturedService[] = SERVICE_CATEGORIES.filter(
  (category: ServiceCategory) => category.featured,
).map(({ name, slug, description, icon }) => ({ name, slug, description, icon }))

/** The one place that knows how a service slug becomes a URL. */
export const serviceHref = (slug: string) => `/services/${slug}`

/** Index of every service category. */
export const ALL_SERVICES_HREF = '/services'

const ICON_BY_SLUG = new Map(SERVICE_CATEGORIES.map((s) => [s.slug, s.icon]))

/**
 * Icon for a category slug, for places that only hold a slug — a contractor
 * record, say. Falls back to a neutral building mark, because the real
 * taxonomy is far wider than the categories declared today and an unknown
 * slug is the expected case, not an error.
 */
export const serviceIconFor = (slug: string): LucideIcon =>
  ICON_BY_SLUG.get(slug) ?? FALLBACK_SERVICE_ICON
